'use client';

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import {
  fetchActiveOrders,
  fetchActivityByEvent,
  fetchDealsActiveOrders,
  fetchDealsOpenPositions,
  fetchDealsPositionHistory,
  fetchFullActivity,
  fetchLastTradesStats,
  fetchOrderHistory,
  fetchTradeHistory,
} from './client';
import type {
  ActiveOrderPageResponse,
  ActivityPageResponse,
  FullActivityListResponse,
  LastTradeStatsResponse,
  OrderHistoryPageResponse,
  TradeHistoryPageResponse,
} from './models';
import type {
  DealsActiveOrdersParams,
  DealsOpenPositionsParams,
  DealsPositionHistoryParams,
} from './types';

// Query keys
export const ORDER_HISTORY_QK = 'order-history';
export const TRADE_HISTORY_QK = 'trade-history';
export const ACTIVE_ORDERS_QK = 'active-orders';
export const ACTIVITY_BY_EVENT_QK = 'activity-by-event';
export const ACTIVITY_INFINITE_PAGE_SIZE = 10;
export const FULL_ACTIVITY_QK = 'full-activity';

export const LAST_TRADES_STATS_QK = 'last-trades-stats';

// Deals query keys
export const DEALS_OPEN_POSITIONS_QK = 'deals-open-positions';
export const DEALS_ACTIVE_ORDERS_QK = 'deals-active-orders';
export const DEALS_POSITION_HISTORY_QK = 'deals-position-history';

/**
 * Hook to fetch order history
 */
export function useOrderHistory(options?: { enabled?: boolean; staleTime?: number }) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<OrderHistoryPageResponse>({
    queryKey: [ORDER_HISTORY_QK],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchOrderHistory();
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: options?.staleTime ?? Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

/**
 * Hook to fetch trade history (no pagination)
 */
export function useTradeHistory(
  outcomeId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<TradeHistoryPageResponse>({
    queryKey: [TRADE_HISTORY_QK, outcomeId],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchTradeHistory(outcomeId);
    },
    enabled: (options?.enabled ?? true) && Boolean(outcomeId) && isAuthenticated,
    staleTime: options?.staleTime,
  });
}

/**
 * Hook to fetch active orders for a specific event outcome
 * GET /history/orders?eventOutcomeId={outcomeId}
 */
export function useActiveOrders(options?: {
  eventOutcomeId?: string;
  enabled?: boolean;
  staleTime?: number;
  page?: number;
  size?: number;
  sort?: string[];
  order?: string[];
}) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<ActiveOrderPageResponse>({
    queryKey: [ACTIVE_ORDERS_QK, options?.eventOutcomeId, options?.page, options?.size],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchActiveOrders({
        eventOutcomeId: options?.eventOutcomeId,
        page: options?.page,
        size: options?.size,
        sort: options?.sort,
        order: options?.order,
      });
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: options?.staleTime ?? Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to fetch activity history for a specific event
 * GET /history/activity/by-event/{eventId}
 */
export function useActivityByEvent(
  eventId: string,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    page?: number;
    size?: number;
  },
) {
  return useQuery<ActivityPageResponse>({
    queryKey: [ACTIVITY_BY_EVENT_QK, eventId, options?.page, options?.size],
    queryFn: () =>
      fetchActivityByEvent(eventId, {
        page: options?.page,
        size: options?.size,
      }),
    enabled: (options?.enabled ?? true) && Boolean(eventId),
    staleTime: options?.staleTime ?? Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to fetch activity history for a specific event with infinite scroll
 * GET /history/activity/by-event/{eventId}
 */
export function useInfiniteActivityByEvent(
  eventId: string,
  options?: {
    enabled?: boolean;
    size?: number;
  },
) {
  const pageSize = options?.size ?? ACTIVITY_INFINITE_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: [ACTIVITY_BY_EVENT_QK, 'infinite', eventId, pageSize],
    queryFn: ({ pageParam = 0 }) =>
      fetchActivityByEvent(eventId, {
        page: pageParam,
        size: pageSize,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.items.length < pageSize) return undefined;
      return lastPage.page + 1;
    },
    enabled: (options?.enabled ?? true) && Boolean(eventId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to fetch full activity history across all events
 * GET /history/activity
 */
export function useFullActivity(options?: {
  enabled?: boolean;
  staleTime?: number;
  page?: number;
  size?: number;
}) {
  const { isAuthenticated } = useAuthContext();

  return useQuery<FullActivityListResponse>({
    queryKey: [FULL_ACTIVITY_QK, options?.page, options?.size],
    queryFn: async () => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchFullActivity({
        page: options?.page,
        size: options?.size,
      });
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: options?.staleTime ?? 30000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * Hook to fetch last trades stats (public, no auth required)
 * GET /history/stats/last-trades
 */
export function useLastTradesStats(options?: { enabled?: boolean; page?: number; size?: number }) {
  const locale = useLocale();
  return useQuery<LastTradeStatsResponse>({
    queryKey: [LAST_TRADES_STATS_QK, options?.page, options?.size, locale],
    queryFn: () => fetchLastTradesStats({ page: options?.page, size: options?.size, locale }),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });
}

// ===== Deals Controller Hooks (Infinite) =====

export function useInfiniteDealsOpenPositions(
  params?: Omit<DealsOpenPositionsParams, 'page' | 'lang'>,
  options?: { enabled?: boolean },
) {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();

  return useInfiniteQuery({
    queryKey: [DEALS_OPEN_POSITIONS_QK, 'infinite', params, locale],
    queryFn: async ({ pageParam = 0 }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchDealsOpenPositions({ ...params, page: pageParam, lang: locale });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useInfiniteDealsActiveOrders(
  params?: Omit<DealsActiveOrdersParams, 'page' | 'lang'>,
  options?: { enabled?: boolean },
) {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();

  return useInfiniteQuery({
    queryKey: [DEALS_ACTIVE_ORDERS_QK, 'infinite', params, locale],
    queryFn: async ({ pageParam = 0 }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchDealsActiveOrders({ ...params, page: pageParam, lang: locale });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: Infinity,
    refetchOnWindowFocus: true,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useInfiniteDealsPositionHistory(
  params?: Omit<DealsPositionHistoryParams, 'page' | 'lang'>,
  options?: { enabled?: boolean },
) {
  const { isAuthenticated } = useAuthContext();
  const locale = useLocale();

  return useInfiniteQuery({
    queryKey: [DEALS_POSITION_HISTORY_QK, 'infinite', params, locale],
    queryFn: async ({ pageParam = 0 }) => {
      if (!isAuthenticated) throw new Error('Unauthorized');
      return fetchDealsPositionHistory({ ...params, page: pageParam, lang: locale });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages - 1) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    enabled: (options?.enabled ?? true) && isAuthenticated,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
  });
}
